import { isEmpty, isEqual, map } from 'es-toolkit/compat';
import React from 'react';
import { QueryParamConfig, StringParam, useQueryParams, withDefault } from 'use-query-params';

import { CohortQueryObject } from 'interfaces/cohort';
import { mapUiJobsFiltersToApiFilters } from 'utils/jobTypeFilters';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { useCurrentLabId } from '../useCurrentLab';
import { accessionQueryParamsSchema, filtersQueryParamsSchema } from './constants';
import { encodeQueryParamsUsingSchema } from './helpers';
import { EncodedFiltersProps } from './types';

export const useEncodedFilters = (
  { experimentResultsSelection }: EncodedFiltersProps = { experimentResultsSelection: undefined }
) => {
  const { labId } = useCurrentLabId();

  const filtersQueryParamsSchemaWithDefaultLab = {
    ...filtersQueryParamsSchema,
    labId: withDefault(StringParam, labId),
  };

  const [urlQueryParams, setQueryParams] = useQueryParams(filtersQueryParamsSchemaWithDefaultLab);
  // Older shared URLs can contain UI-only job-type sentinels. Normalize them as they enter
  // application state so every request serializer sees the backend's canonical filter contract.
  const queryParams = React.useMemo(
    () => ({
      ...urlQueryParams,
      jobsFilters: urlQueryParams.jobsFilters
        ? mapUiJobsFiltersToApiFilters(urlQueryParams.jobsFilters)
        : urlQueryParams.jobsFilters,
      datasetJobFilters: urlQueryParams.datasetJobFilters
        ? mapUiJobsFiltersToApiFilters(urlQueryParams.datasetJobFilters)
        : urlQueryParams.datasetJobFilters,
    }),
    [urlQueryParams]
  );

  // Can be used to generate optimistic responses to new encoded filters queries
  const generateEncodedParams = React.useCallback(
    (
      newParams: Partial<typeof queryParams>,
      additionalParams: Record<string, any> = {},
      additionalSchemaDefinitions: Record<string, QueryParamConfig<any, any>> = {}
    ) =>
      encodeQueryParamsUsingSchema(
        {
          cohortId: newParams?.cohortId ?? queryParams.cohortId,
          filters: newParams?.filters ?? queryParams.filters,
          features: newParams?.features ?? queryParams.features,
          clinicalData: newParams?.clinicalData ?? queryParams.clinicalData,
          labId: newParams?.labId ?? queryParams.labId ?? labId,
          page: newParams?.page ?? queryParams.page ?? 1,
          pageSize: newParams?.pageSize ?? queryParams.pageSize ?? DEFAULT_PAGE_SIZE,
          sortBy: newParams?.sortBy ?? queryParams.sortBy,
          resultsMode: newParams?.resultsMode ?? queryParams.resultsMode,
          experimentResultsSelection: experimentResultsSelection,
          slidesMode: newParams?.slidesMode ?? queryParams.slidesMode ?? false,
          ...additionalParams,
          ...(newParams?.caseIdsToInclude || queryParams.caseIdsToInclude
            ? { caseIdsToInclude: newParams?.caseIdsToInclude ?? queryParams.caseIdsToInclude }
            : {}),
          ...(newParams?.slideIdsToInclude || queryParams.slideIdsToInclude
            ? { slideIdsToInclude: newParams?.slideIdsToInclude ?? queryParams.slideIdsToInclude }
            : {}),
          ...(newParams?.uploadFilters || queryParams.uploadFilters
            ? { uploadFilters: newParams?.uploadFilters ?? queryParams.uploadFilters }
            : {}),
          datasetJobFilters: newParams?.datasetJobFilters ?? queryParams.datasetJobFilters,
          datasetJobFiltersIncludeExclude:
            newParams?.datasetJobFiltersIncludeExclude ?? queryParams.datasetJobFiltersIncludeExclude,
          skipPagination: newParams?.skipPagination ?? queryParams.skipPagination ?? false,
          jobsFilters: newParams?.jobsFilters ?? queryParams.jobsFilters,
          jobsFiltersIncludeExclude: newParams?.jobsFiltersIncludeExclude ?? queryParams.jobsFiltersIncludeExclude,
        },
        { ...filtersQueryParamsSchema, ...additionalSchemaDefinitions },
        {
          arrayFormat: 'repeat',
        }
      ),
    [labId, queryParams, experimentResultsSelection]
  );
  const encodedFilters = generateEncodedParams(queryParams);

  const resetAllFilters = () => {
    setQueryParams({
      filters: null,
      features: null,
      clinicalData: null,
      queryId: null,
      searchTerm: null,
      caseIdsToInclude: [],
      slideIdsToInclude: [],
    });
  };

  const emptyFiltersExceptStudy: Partial<typeof queryParams> = {
    filters: queryParams.filters?.studyId ? { studyId: queryParams.filters?.studyId } : null,
    features: null,
    clinicalData: null,
    queryId: null,
    searchTerm: null,
    caseIdsToInclude: [],
    slideIdsToInclude: [],
  };

  const resetAllFiltersExceptStudy = () => {
    setQueryParams(emptyFiltersExceptStudy);
  };

  const areFiltersAppliedExceptStudy: boolean = !isEqual(
    {
      filters: !isEmpty(queryParams.filters) ? queryParams.filters : null,
      features: queryParams.features,
      clinicalData: queryParams.clinicalData,
      queryId: queryParams.queryId,
      searchTerm: queryParams.searchTerm,
      caseIdsToInclude: queryParams.caseIdsToInclude,
      slideIdsToInclude: queryParams.slideIdsToInclude,
    },
    emptyFiltersExceptStudy
  );

  const convertToQueryObject = (params: Partial<typeof queryParams>): CohortQueryObject => {
    return {
      filters: params?.filters,
      clinicalData: params?.clinicalData,
      features: params?.features,
      searchTerm: params?.searchTerm,
      caseIdsToInclude: map(params.caseIdsToInclude, (id: string | number) =>
        typeof id === 'string' ? Number(id) : id
      ),
      slideIdsToInclude: params?.slideIdsToInclude,
      slidesMode: params?.slidesMode,
    };
  };

  return {
    queryParams,
    setQueryParams,
    encodedFilters,
    generateEncodedParams,
    queryParamsSchema: filtersQueryParamsSchemaWithDefaultLab,
    resetAllFiltersExceptStudy,
    resetAllFilters,
    convertToQueryObject,
    areFiltersAppliedExceptStudy,
    emptyFiltersExceptStudy,
  };
};

export type GenerateEncodedParams = ReturnType<typeof useEncodedFilters>['generateEncodedParams'];

export const useFiltersForAccessions = () => {
  const { labId } = useCurrentLabId();

  const [queryParams, setQueryParams] = useQueryParams(accessionQueryParamsSchema);

  const encodedFilters = encodeQueryParamsUsingSchema(
    {
      dateOfScanFrom: queryParams.dateOfScanFrom,
      dateOfScanTo: queryParams.dateOfScanTo,
      accessionId: queryParams.accessionId,
      medicalRecordNumber: queryParams.medicalRecordNumber,
      labId,
      page: queryParams.page,
    },
    accessionQueryParamsSchema
  );

  return { queryParams, setQueryParams, encodedFilters };
};
